import { AlertCircle, LoaderCircle } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import AdminLayout from "../components/AdminLayout";
import LowStockPanel from "../dashboard/LowStockPanel";
import OrderOverview from "../dashboard/OrderOverview";
import PopularProducts from "../dashboard/PopularProducts";
import QuickActions from "../dashboard/QuickActions";
import RecentOrders from "../dashboard/RecentOrders";
import RevenueOverview from "../dashboard/RevenueOverview";
import SalesChart from "../dashboard/SalesChart";
import UpcomingDeliveries from "../dashboard/UpcomingDeliveries";
import { fetchAdminDashboard } from "../api/adminDashboardApi";
import { getStoredUser } from "../../utils/auth";

function firstName() {
  const user = getStoredUser();
  const name = user?.displayName || user?.username || "Morgan";
  return name.split(" ").filter(Boolean)[0] || "Morgan";
}

function AdminDashboardPage() {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [period, setPeriod] = useState("week");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const displayName = firstName();

  const loadDashboard = useCallback(async (signal) => {
    setIsLoading(true);
    setError("");
    try {
      setDashboard(await fetchAdminDashboard({ period, signal }));
    } catch (requestError) {
      if (requestError.name !== "AbortError") {
        if (process.env.NODE_ENV === "development") console.error("Dashboard data request failed", requestError);
        setError("Dashboard data could not be loaded. Please try again.");
      }
    } finally {
      if (!signal?.aborted) setIsLoading(false);
    }
  }, [period]);

  useEffect(() => {
    const controller = new AbortController();
    loadDashboard(controller.signal);
    return () => controller.abort();
  }, [loadDashboard]);

  return (
    <AdminLayout>
      <div className="admin-page">
        <section className="admin-page-heading">
          <div>
            <p className="eyebrow">Bakery overview</p>
            <h1>Good morning, {displayName}</h1>
            <p>Here is what is happening with SugaLumps Cupcakery today.</p>
          </div>

          <button type="button" className="btn btn--primary" onClick={() => navigate("/admin/orders")}>Create New Order</button>
        </section>

        {error && <div className="admin-menu-error"><AlertCircle size={18} /><span>{error}</span></div>}
        {isLoading && !dashboard ? <div className="inventory-loading"><LoaderCircle size={22} className="admin-spin" /> Loading dashboard...</div> : null}

        <RevenueOverview sales={dashboard?.sales} />
        <OrderOverview counts={dashboard?.order_counts} />

        <section className="admin-dashboard-block">
          <div className="admin-dashboard-block__heading"><div><h2>Quick Actions</h2><p>Jump directly into common bakery tasks.</p></div></div>
          <QuickActions />
        </section>

        <div className="admin-dashboard-grid admin-dashboard-grid--wide-left">
          <SalesChart overview={dashboard?.sales_overview} period={period} onPeriodChange={setPeriod} />
          <UpcomingDeliveries deliveries={dashboard?.todays_deliveries} />
        </div>

        <RecentOrders orders={dashboard?.recent_orders} />

        <div className="admin-dashboard-grid">
          <PopularProducts products={dashboard?.popular_products} />
          <LowStockPanel items={dashboard?.low_stock} />
        </div>
      </div>
    </AdminLayout>
  );
}

export default AdminDashboardPage;
